import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Table, Tag, Button, Input, Select, Alert, Space, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import DataCard from '../page/DataCard';
import PageFilter from '../page/PageFilter';
import { errorText } from './taxonomyApi';

const { Text } = Typography;
const PAGE_SIZE = 20;

/**
 * Pending from Policy Hub: framework controls that have no ISO 27002 mapping yet, per loaded framework,
 * plus the existing ISO 27001 pending list as its own chip (`isoPendingNode`, rendered by the page).
 * Saves go through POST /control-taxonomy/mappings/bulk-assign.
 */
const PendingFromPolicyHub = ({ api, frameworks, counts, isoPendingNode, onChanged }) => {
  const loaded = frameworks.filter((f) => f.loaded && f.key !== 'iso27001');
  const countOf = (key) => counts?.frameworks?.[key] ?? 0;
  const [chip, setChip] = useState(() => {
    const first = loaded.find((f) => countOf(f.key) > 0);
    return first ? first.key : 'iso27001';
  });
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [state, setState] = useState({ status: 'loading', rows: [], total: 0, message: null });
  const [drafts, setDrafts] = useState({}); // targetRef -> ISO refs
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [isoOptions, setIsoOptions] = useState({ status: 'idle', options: [] });
  const seq = useRef(0);

  const isFramework = chip !== 'iso27001';

  const load = useCallback(async () => {
    if (!isFramework) return;
    const my = ++seq.current;
    setState((s) => ({ ...s, status: 'loading' }));
    try {
      const { rows, total } = await api.unmapped({ framework: chip, search: query, page, pageSize: PAGE_SIZE });
      if (my === seq.current) setState({ status: 'ready', rows, total, message: null });
    } catch (err) {
      if (my === seq.current) setState({ status: 'error', rows: [], total: 0, message: errorText(err, 'Could not load unmapped controls.') });
    }
  }, [api, chip, query, page, isFramework]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!isFramework || isoOptions.status !== 'idle') return;
    setIsoOptions({ status: 'loading', options: [] });
    api.isoControls()
      .then((options) => setIsoOptions({ status: 'ready', options }))
      .catch((err) => setIsoOptions({ status: 'error', options: [], message: errorText(err, 'Could not load ISO 27002 controls.') }));
  }, [api, isFramework, isoOptions.status]);

  const pick = (k) => { setChip(k); setPage(1); setSearch(''); setQuery(''); setDrafts({}); setSaveError(null); };

  const submit = async (refs) => {
    const assignments = refs.filter((r) => drafts[r]?.length).map((r) => ({ targetRef: r, isoControls: drafts[r] }));
    if (!assignments.length) return;
    setSaving(true);
    setSaveError(null);
    try {
      await api.bulkAssign(chip, assignments);
      setDrafts((d) => { const next = { ...d }; assignments.forEach((a) => delete next[a.targetRef]); return next; });
      await load();
      if (onChanged) await onChanged();
    } catch (err) {
      setSaveError(errorText(err, 'Could not save the assignments.'));
    } finally {
      setSaving(false);
    }
  };

  const options = [
    { value: 'iso27001', label: `ISO 27001 (${counts?.iso27001 ?? 0})` },
    ...loaded.map((f) => ({ value: f.key, label: `${f.label} (${countOf(f.key)})` })),
  ];
  const fwLabel = loaded.find((f) => f.key === chip)?.label || chip;
  const readyDrafts = Object.keys(drafts).filter((r) => drafts[r]?.length);

  const columns = [
    { title: 'Control', dataIndex: 'ref', key: 'ref', width: 120, render: (v) => <Tag>{v}</Tag> },
    { title: 'Name', dataIndex: 'name', key: 'name', ellipsis: { showTitle: true } },
    {
      title: 'Map to ISO 27002 controls',
      key: 'iso',
      width: 360,
      render: (_, r) => (
        <Select
          mode="multiple"
          showSearch
          style={{ width: '100%' }}
          placeholder="Choose ISO controls"
          loading={isoOptions.status === 'loading'}
          optionFilterProp="label"
          value={drafts[r.ref] || []}
          onChange={(v) => setDrafts((d) => ({ ...d, [r.ref]: v }))}
          options={isoOptions.options.map((o) => ({ value: o.ref, label: `${o.ref} ${o.name}`.trim() }))}
          data-testid={`pending-select-${r.ref}`}
        />
      ),
    },
    {
      title: '',
      key: 'save',
      width: 90,
      render: (_, r) => (
        <Button size="small" type="primary" disabled={!drafts[r.ref]?.length || saving} onClick={() => submit([r.ref])} data-testid={`pending-save-${r.ref}`}>Save</Button>
      ),
    },
  ];

  return (
    <>
      <PageFilter value={chip} onChange={pick} options={options} />
      {!isFramework && isoPendingNode}
      {isFramework && (
        <DataCard size="small">
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Space wrap>
              <Input
                placeholder={`Search ${fwLabel} controls`}
                prefix={<SearchOutlined />}
                value={search}
                onChange={(e) => { setSearch(e.target.value); if (!e.target.value) { setQuery(''); setPage(1); } }}
                onPressEnter={() => { setQuery(search.trim()); setPage(1); }}
                allowClear
                style={{ width: 320 }}
              />
              <Button onClick={() => { setQuery(search.trim()); setPage(1); }}>Search</Button>
              <Button type="primary" disabled={!readyDrafts.length || saving} loading={saving} onClick={() => submit(readyDrafts)} data-testid="pending-save-all">
                {`Save ${readyDrafts.length} assignment${readyDrafts.length === 1 ? '' : 's'}`}
              </Button>
            </Space>
            {saveError && <Alert type="error" showIcon data-testid="pending-save-error" message={saveError} />}
            {isoOptions.status === 'error' && <Alert type="error" showIcon message={isoOptions.message} />}
            {state.status === 'error' && <Alert type="error" showIcon data-testid="pending-error" message={state.message} action={<Button size="small" onClick={load}>Retry</Button>} />}
            <Table
              rowKey="ref"
              size="small"
              columns={columns}
              dataSource={state.rows}
              loading={state.status === 'loading'}
              locale={{ emptyText: state.status === 'ready' ? `Every loaded ${fwLabel} control is mapped to ISO 27002.` : '' }}
              pagination={{ current: page, pageSize: PAGE_SIZE, total: state.total, showSizeChanger: false, onChange: setPage }}
            />
            <Text type="secondary">Mappings you save here appear in the Cross-Framework Mapping tab.</Text>
          </Space>
        </DataCard>
      )}
    </>
  );
};

export default PendingFromPolicyHub;
